import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { Call, Lead, AgentState } from '@marnic/shared';

export type SoftphoneStatus =
  | 'uninitialized'
  | 'registering'
  | 'ready'
  | 'ringing_inbound'
  | 'connecting'
  | 'on_call'
  | 'wrap_up'
  | 'error';

interface SoftphoneState {
  status: SoftphoneStatus;
  identity: string | null;
  agentState: AgentState;
  currentCall: Call | null;
  currentLead: Lead | null;
  currentCallbackId: string | null;
  callDraftNumber: string;
  selectedCallerId: string;
  selectedCallerProvider: 'TWILIO' | 'VOIPLINE' | null;
  isMuted: boolean;
  isOnHold: boolean;
  callStartedAt: number | null;
  errorMessage: string | null;
  audioInputDeviceId: string | null;
  audioOutputDeviceId: string | null;
  campaignContext: { campaignId: string; campaignLeadId?: string } | null;
  lastDispositionedAt: number | null;

  setStatus: (s: SoftphoneStatus) => void;
  setIdentity: (id: string) => void;
  setAgentState: (s: AgentState) => void;
  startCall: (call: Call, lead: Lead | null, ctx?: { campaignId: string; campaignLeadId?: string } | null) => void;
  markAnswered: () => void;
  clearRingingCall: () => void;
  endCall: () => void;
  setMuted: (m: boolean) => void;
  setHold: (h: boolean) => void;
  setError: (msg: string | null) => void;
  setAudioInputDevice: (id: string | null) => void;
  setAudioOutputDevice: (id: string | null) => void;
  setLead: (l: Lead | null) => void;
  setCampaignContext: (ctx: { campaignId: string; campaignLeadId?: string } | null) => void;
  setCallbackId: (id: string | null) => void;
  setCallDraftNumber: (number: string) => void;
  setSelectedCallerId: (number: string, provider?: 'TWILIO' | 'VOIPLINE' | null) => void;
  noteDisposition: () => void;
}

export const useSoftphone = create<SoftphoneState>()(
  persist((set) => ({
  status: 'uninitialized',
  identity: null,
  agentState: 'offline',
  currentCall: null,
  currentLead: null,
  currentCallbackId: null,
  callDraftNumber: '',
  selectedCallerId: '',
  selectedCallerProvider: null,
  isMuted: false,
  isOnHold: false,
  callStartedAt: null,
  errorMessage: null,
  audioInputDeviceId: null,
  audioOutputDeviceId: null,
  campaignContext: null,
  lastDispositionedAt: null,

  setStatus: (status) => set({ status }),
  setIdentity: (identity) => set({ identity }),
  setAgentState: (agentState) => set({ agentState }),
  startCall: (currentCall, currentLead, ctx = null) => {
    const isRingingInbound = currentCall.direction === 'inbound' && currentCall.status === 'ringing';
    set({
      currentCall,
      currentLead,
      callStartedAt: isRingingInbound ? null : Date.now(),
      status: isRingingInbound ? 'ringing_inbound' : 'on_call',
      isMuted: false,
      isOnHold: false,
      campaignContext: ctx,
    });
  },
  markAnswered: () =>
    set({
      callStartedAt: Date.now(),
      status: 'on_call',
    }),
  clearRingingCall: () =>
    set({
      currentCall: null,
      currentLead: null,
      callStartedAt: null,
      status: 'ready',
      isMuted: false,
      isOnHold: false,
      campaignContext: null,
    }),
  endCall: () =>
    set({
      currentCall: null,
      callStartedAt: null,
      status: 'wrap_up',
      isMuted: false,
      isOnHold: false,
    }),
  setMuted: (isMuted) => set({ isMuted }),
  setHold: (isOnHold) => set({ isOnHold }),
  setError: (errorMessage) =>
    set({ errorMessage, status: errorMessage ? 'error' : 'ready' }),
  setAudioInputDevice: (audioInputDeviceId) => set({ audioInputDeviceId }),
  setAudioOutputDevice: (audioOutputDeviceId) => set({ audioOutputDeviceId }),
  setLead: (currentLead) => set({ currentLead }),
  setCampaignContext: (campaignContext) => set({ campaignContext }),
  setCallbackId: (currentCallbackId) => set({ currentCallbackId }),
  setCallDraftNumber: (callDraftNumber) => set({ callDraftNumber }),
  setSelectedCallerId: (selectedCallerId, selectedCallerProvider = null) => set({ selectedCallerId, selectedCallerProvider }),
  noteDisposition: () =>
    set((s) => ({
      lastDispositionedAt: Date.now(),
      status: s.status === 'wrap_up' ? 'ready' : s.status,
      currentLead: null,
      campaignContext: null,
      currentCallbackId: null,
    })),
}), {
  name: 'marnic.softphone',
  // Only persist the user's "Call from" caller-ID choice. Call state,
  // mute, current lead, etc. must reset on refresh.
  partialize: (s) => ({
    selectedCallerId: s.selectedCallerId,
    selectedCallerProvider: s.selectedCallerProvider,
    audioInputDeviceId: s.audioInputDeviceId,
    audioOutputDeviceId: s.audioOutputDeviceId,
  }),
}));
